import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAdminSeason,
  deleteAdminSeason,
  getAdminSeasons,
  updateAdminSeason,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { unscopedKey } from "@/lib/queryClient";
import { ApiError } from "@/lib/api";
import type { Season } from "@/lib/types";

/**
 * `/admin/seasons` — global-admin season management.
 *
 * Seasons are globally shared objects: the system admin defines a
 * season once here and every team references the same list. They are
 * no longer a team sub-resource — a season created here is immediately
 * usable by all teams when they create matches.
 *
 * Delete is rejected by the backend (409) when a match still
 * references the season; the error surfaces inline in the confirm
 * dialog.
 */

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

function seasonDateRange(s: Season): string {
  if (s.starts_on && s.ends_on) return `${s.starts_on} → ${s.ends_on}`;
  if (s.starts_on) return `${s.starts_on} → ?`;
  if (s.ends_on) return `? → ${s.ends_on}`;
  return "no dates set";
}

export function AdminSeasonsPage() {
  const queryClient = useQueryClient();
  const seasonsQuery = useQuery({
    queryKey: unscopedKey("admin-seasons"),
    queryFn: getAdminSeasons,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Season | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Season | null>(null);

  const seasons = seasonsQuery.data ?? [];
  // Refresh both the admin list AND `/api/me` — the match season picker
  // (`MatchEditModal`) reads the global `me.seasons` array, which has a
  // 5-minute stale time, so a create/edit/delete here must invalidate
  // `me` or the picker shows the old season set until the next refetch.
  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-seasons"),
    });
    void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
  };

  return (
    <div data-testid="admin-seasons" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-base font-display uppercase tracking-wider text-ink">
          Seasons
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-seasons-new"
            className="rounded border border-accent/40 text-accent hover:bg-accent/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            New season
          </button>
          <span className="text-[0.6rem] font-mono text-ink-muted">
            {seasonsQuery.isFetching
              ? "Refreshing…"
              : `${seasons.length} season${seasons.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </header>

      <p className="text-xs text-ink-muted">
        Seasons are shared across every team. Define a season once here and
        all teams can assign matches to it.
      </p>

      {seasonsQuery.error ? (
        <p
          data-testid="admin-seasons-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(seasonsQuery.error)}
        </p>
      ) : null}

      {seasonsQuery.isLoading ? (
        <p className="text-xs font-mono text-ink-muted">Loading…</p>
      ) : seasons.length === 0 ? (
        <p
          data-testid="admin-seasons-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No seasons yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {seasons.map((s) => (
            <li
              key={s.id}
              data-testid="admin-season-row"
              data-season-id={s.id}
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-surface/40 px-2 py-1.5 text-xs"
            >
              <div className="min-w-0">
                <div className="text-ink truncate">{s.name}</div>
                <div className="text-[0.6rem] font-mono text-ink-muted">
                  {seasonDateRange(s)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setEditTarget(s)}
                  data-testid="admin-season-edit"
                  className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(s)}
                  data-testid="admin-season-delete"
                  className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {createOpen ? (
        <SeasonComposer
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}
      {editTarget ? (
        <SeasonComposer
          mode="edit"
          season={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void invalidate();
          }}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteSeasonDialog
          season={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function SeasonComposer({
  mode,
  season,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  season?: Season;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(season?.name ?? "");
  const [startsOn, setStartsOn] = useState(season?.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(season?.ends_on ?? "");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      mode === "create"
        ? createAdminSeason({ name, starts_on: startsOn, ends_on: endsOn })
        : updateAdminSeason(season!.id, {
            name,
            starts_on: startsOn,
            ends_on: endsOn,
          }),
    onSuccess: onSaved,
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <ConfirmDialog
      title={mode === "create" ? "New season" : "Edit season"}
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-seasons-composer-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Starts on
              </span>
              <TkticDateInput
                value={startsOn}
                onChange={setStartsOn}
                data-testid="admin-seasons-composer-starts"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Ends on
              </span>
              <TkticDateInput
                value={endsOn}
                onChange={setEndsOn}
                data-testid="admin-seasons-composer-ends"
              />
            </label>
          </div>
          {error ? (
            <p
              data-testid="admin-seasons-composer-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={
        mut.isPending
          ? "Saving…"
          : mode === "create"
            ? "Create season"
            : "Save changes"
      }
      onConfirm={() => {
        setError(null);
        if (!name.trim()) {
          setError("Name is required.");
          return;
        }
        if (startsOn && endsOn && startsOn > endsOn) {
          setError("Start date must be on or before end date.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-composer"
    />
  );
}

function DeleteSeasonDialog({
  season,
  onClose,
  onDeleted,
}: {
  season: Season;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => deleteAdminSeason(season.id),
    onSuccess: onDeleted,
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <ConfirmDialog
      title="Delete season"
      danger
      message={
        <div className="flex flex-col gap-2 text-xs">
          <p className="text-ink">
            Delete <span className="font-display">{season.name}</span>? This
            removes it for every team.
          </p>
          {error ? (
            <p
              data-testid="admin-seasons-delete-error"
              className="text-destructive font-mono"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Deleting…" : "Delete season"}
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-delete"
    />
  );
}
